/**
 * O caso: próximo passo, fila de automação, pendências, fatos, minuta,
 * modo da IA, quem cuida, ficha, etapa, arquivar/apagar, novo caso e a fila.
 *
 * Um pedaço do objeto de ações do `Provedor` (`estado.jsx`), cortado por área.
 * Recebe o contexto uma vez, dentro do `useMemo` de lá: a identidade de cada
 * ação continua estável, como era no objeto único.
 */
import * as api from "../api.js";
import { gravou } from "../gravou.js";
import { confirmar, pedirTexto } from "@/componentes/pergunta.tsx";

export function acoesDoCaso(c) {
  const { abortoMinuta, acoesRef, avisar, carregarMesa, casoAberto, listaDaMesa, mudar, navRef, recarregarCaso, ref, tentar } = c;
  return {
    /**
     * O próximo passo do caso, executado.
     *
     * `pauta.passos` chega do servidor com rota, método e permissão prontos —
     * nove rotas úteis que não tinham um único chamador na tela, incluindo o
     * portão do manual (aprovar o checklist) e a extração da reunião, que
     * ficava guardada no JSON e morria lá. Um despachante só, e nenhuma função
     * por rota: o passo diz onde bater.
     *
     * Três passos NÃO são POST cego, porque o que eles fazem é gravar em nome
     * de alguém: mandar mensagem ao cliente, cobrar documento e montar peça
     * passam pelo caminho que já existe — o rascunho, que a pessoa lê antes de
     * enviar, e a montagem em fluxo, que mostra o texto nascendo.
     */
    executarPasso: async (passo) => {
      if (!passo || !casoAberto()) return null;
      if (
        passo.confirmar &&
        !(await confirmar({ titulo: `${passo.rotulo}?`, texto: passo.detalhe, acao: passo.rotulo, perigo: true }))
      )
        return null;

      /* Passo que precisa de dado que só a pessoa tem (a data da reunião, a
         transcrição, o número do protocolo) chega com `abre`: gravar direto
         mandaria um POST vazio e voltaria erro de validação. */
      if (passo.abre) return navRef.current.ir(passo.abre);

      /* Cobrar é o texto que a régua escreveu indo para o campo de escrever:
         mandar sozinho tiraria da pessoa a última leitura antes do cliente. */
      if (passo.acao === "cobrar_documentos") {
        const texto = ref.current.caso?.pauta?.cobranca?.texto;
        if (!texto) return avisar("Nada a cobrar agora.");
        acoesRef.current.escreverNoRascunho(texto);
        return avisar("Cobrança no campo de escrever — revise e mande.");
      }
      if (passo.acao === "responder") {
        navRef.current.ajustar({ painel: null });
        return avisar("A última mensagem é do cliente — responda na conversa.");
      }
      if (passo.acao === "minutar") {
        navRef.current.ajustar({ peticao: true });
        return acoesRef.current.gerarMinuta();
      }
      /* Preencher a ficha é aplicar a PROPOSTA que o servidor já montou, com a
         fonte de cada valor: o mesmo dado que o botão mostra é o que grava. */
      if (passo.acao === "aplicar_ficha") {
        const campos = ref.current.caso?.pauta?.ficha?.campos ?? [];
        if (!campos.length) return avisar("Nada para preencher.");
        return tentar("passo", async () => {
          await api.mandar(passo.rota, { campos: Object.fromEntries(campos.map((c) => [c.campo, c.valor])) });
          await recarregarCaso();
          await carregarMesa();
          avisar(campos.length === 1 ? "1 campo preenchido na ficha." : `${campos.length} campos preenchidos na ficha.`);
        });
      }
      /* Sugerir NÃO grava: devolve proposta. Gravar é o segundo clique — é a
         mesma regra da fila, nada entra na ficha sem alguém dizer que entra. */
      if (passo.acao === "sugerir_ficha")
        return tentar("passo", async () => {
          const r = await api.mandar(passo.rota);
          const campos = r?.campos ?? {};
          const linhas = Object.entries(campos);
          if (!linhas.length) return avisar("A conversa não deu material para a ficha.");
          const ok = await confirmar({
            titulo: linhas.length === 1 ? "Preencher 1 campo da conversa?" : `Preencher ${linhas.length} campos da conversa?`,
            texto: linhas.map(([k, v]) => `${k}: ${v}`).join(" · "),
            acao: "Preencher",
          });
          if (!ok) return null;
          await api.mandar(`/api/leads/${casoAberto()}/qualificar`, { campos });
          await recarregarCaso();
          await carregarMesa();
          return avisar(linhas.length === 1 ? "1 campo preenchido na ficha." : `${linhas.length} campos preenchidos na ficha.`);
        });

      /* `corpo` é do SERVIDOR: mover etapa precisa do destino, e quem sabe o
         destino é quem montou o passo — a tela repetindo "qualificado" aqui
         seria uma segunda verdade sobre o funil. */
      return tentar("passo", async () => {
        await api.mandar(passo.rota, passo.corpo);
        await recarregarCaso();
        await carregarMesa();
        avisar(`${passo.rotulo}: feito.`);
      });
    },

    /**
     * Aprovar ou cancelar o item da fila do agente, de dentro do caso.
     *
     * Com `AGENTE_AUTOMATICO=0` nada grava fora do CRM sem aprovação — e não
     * havia onde aprovar: o item enfileirado para o Astrea ficava lá para
     * sempre. Aprovar pergunta antes porque o efeito é fora do CRM.
     */
    decidirFila: async (item, aprovar) => {
      const ok = await confirmar({
        titulo: aprovar ? `Aprovar "${item.tarefa}"?` : `Cancelar "${item.tarefa}"?`,
        texto: aprovar ? "Isto grava fora do CRM (Astrea, WhatsApp). Não tem desfazer." : "O item sai da fila sem rodar.",
        acao: aprovar ? "Aprovar" : "Cancelar o item",
        perigo: !aprovar,
      });
      if (!ok) return;
      await tentar("fila", async () => {
        await api.mandar(`/api/fila/${item.id}/${aprovar ? "aprovar" : "cancelar"}`);
        await recarregarCaso();
        avisar(aprovar ? "Aprovado — o agente vai executar." : "Item cancelado.");
      });
    },

    /**
     * Concluir a pendência que é TAREFA.
     *
     * Só `origem: "tarefa"` passa por aqui: o `id` da pendência é o da tabela de
     * origem (contrato de `src/pendencias.ts`), e mandar o id de uma peça para
     * `/api/tasks/:id/complete` concluiria a tarefa de outra pessoa. Prazo de
     * peça se resolve na esteira, pelo link que a própria pendência traz.
     */
    concluirPendencia: async (p) => {
      if (p.origem !== "tarefa") return;
      if (!(await confirmar({ titulo: `Concluir "${p.titulo}"?`, texto: "Sai da lista de prazos.", acao: "Concluir" }))) return;
      await tentar("pendencia", async () => {
        await api.mandar(`/api/tasks/${p.id}/complete`);
        await recarregarCaso();
        await carregarMesa();
      });
    },

    alternarFato: async (id) => {
      const fato = (ref.current.provas ?? []).find((p) => p.id === id);
      if (!fato) return;
      await tentar("fato", async () => {
        await api.marcarProva(id, !fato.na_peca);
        await recarregarCaso();
      });
    },

    /**
     * Monta a minuta do acervo com o texto aparecendo enquanto o modelo
     * escreve. O servidor grava a peça no fim — é o que faz o .docx do editor
     * virar a versão nova; aqui o `rascunhoPeca` é só o que a tela mostra
     * nascendo.
     */
    /* Quem está montando agora: é o que o botão Cancelar corta. */
    /* Montar leva perto de um minuto de modelo, e o texto que já apareceu na
       tela não é gravado em lugar nenhum: cortar recomeça do zero. Cobre os
       dois botões que chamam daqui — o da `PecaNascendo` e o "Cancelar a
       montagem" da barra do `EditorPeca`. */
    cancelarMinuta: async () => {
      const ok = await confirmar({
        titulo: "Cortar a montagem da peça?",
        texto: "O texto que já apareceu é jogado fora e a peça volta ao que era. Montar de novo começa do zero.",
        acao: "Cortar a montagem",
        perigo: true,
      });
      if (ok) abortoMinuta.current?.abort();
    },

    gerarMinuta: (opcoes = {}) =>
      tentar("minuta", async () => {
        let peca = (ref.current.caso?.pecas ?? [])[0];
        if (!peca) peca = await api.criarPeca(casoAberto(), "inicial");
        const id = peca.id ?? peca.peca?.id;
        /*
         * Montagem em curso NÃO é cancelada por um segundo pedido.
         *
         * `abortoMinuta.current?.abort()` estava no começo: qualquer segunda
         * chamada — o duplo clique de quem achou que não pegou, ou a tela da
         * petição pedindo a sua ao abrir — matava a primeira. E como cancelar
         * é caminho legítimo ("cancelar tem de deixar a peça como estava"), o
         * servidor não gravava nada e ninguém via erro: a peça simplesmente
         * não aparecia. Montar uma inicial pelo assistente leva um minuto; o
         * segundo pedido tem de esperar, não derrubar o primeiro.
         */
        if (abortoMinuta.current && !abortoMinuta.current.signal.aborted) {
          avisar("A minuta já está sendo montada — o texto aparece aqui quando ela terminar.");
          return null;
        }
        mudar({ rascunhoPeca: "" });
        const controle = new AbortController();
        abortoMinuta.current = controle;
        try {
          const fim = await api.montarMinutaEmFluxo(id, {
            sinal: controle.signal,
            aoPedaco: (pedaco) => {
              mudar((a) => ({ rascunhoPeca: (a.rascunhoPeca ?? "") + pedaco }));
              /* Quem abriu o editor escreve o pedaço direto no documento. */
              opcoes.aoPedaco?.(pedaco);
            },
          });
          if (fim?.pendencias?.length) avisar(`Minuta montada com ${fim.pendencias.length} ponto(s) marcados [A CONFERIR].`);
          await recarregarCaso();
          return fim;
        } catch (e) {
          if (controle.signal.aborted) {
            avisar("Montagem cancelada — a peça ficou como estava.");
            return null;
          }
          throw e;
        } finally {
          if (abortoMinuta.current === controle) abortoMinuta.current = null;
        }
      }),

    /* O .docx da peça, na ida e na volta. Fica em ações — quem fala com a API
       é este arquivo, e o editor só recebe as duas funções. Gravar recarrega
       o caso para a versão e o horário do cabeçalho baterem com o banco. */
    documentoDaPeca: (id) => api.documentoDaPeca(id),
    salvarDocumentoDaPeca: async (id, bytes) => {
      const r = await api.salvarDocumentoDaPeca(id, bytes);
      await recarregarCaso();
      return r;
    },
    pedirEdicoesIA: (id, pedido, opcoes) => api.pedirEdicoesIA(id, pedido, opcoes),

    /* O modo da IA NESTE caso — `null` volta a herdar o do escritório. Piloto
       é o que "delegar" era: o servidor abre a condução e a IA age na hora. */
    /* Copiloto e Piloto vão com o agente; sem ele, o servidor usa o da etapa. */
    escolherModoDoCaso: (modo, agente_id = null) =>
      tentar("delegar", async () => {
        await api.definirModoDoCaso(casoAberto(), modo, agente_id);
        navRef.current.ajustar({ delegar: null });
        await recarregarCaso();
        await carregarMesa();
      }),

    /* Um agente de recepção continua a conversa (o lead que veio do LiderHub). */
    recepcaoAMao: (agente_id) =>
      tentar("delegar", async () => {
        await api.recepcaoAMao(casoAberto(), agente_id);
        navRef.current.ajustar({ delegar: null });
        await recarregarCaso();
        await carregarMesa();
      }),

    retomar: () =>
      tentar("retomar", async () => {
        await api.retomar(casoAberto());
        navRef.current.ajustar({ delegar: null });
        await recarregarCaso();
        await carregarMesa();
      }),

    /*
     * "É meu": o atendimento sem dono passa a ser de quem está atendendo.
     *
     * É o gesto mais comum da mesa e custava abrir o painel, achar "Transferir
     * atendimento" e procurar o próprio nome numa lista de quem já está cheio.
     * Assumir é transferir para si — a mesma escrita, a mesma trilha, um
     * clique. O nome é o DA PESSOA no fluxo (`usuario.pessoa`), não o do
     * login: é ele que a ficha guarda e que a pastilha "Meus" compara.
     */
    assumirCaso: async () => {
      const eu = ref.current.usuario?.pessoa;
      if (!eu) return avisar("Seu acesso não está ligado a uma pessoa da equipe — peça para vincularem em Equipe e acessos.");
      const dono = ref.current.caso?.registro?.dados?.colaborador;
      if (dono && dono.toUpperCase() === eu.toUpperCase()) return avisar("Este atendimento já é seu.");
      if (
        dono &&
        !(await confirmar({
          titulo: `Assumir de ${dono}?`,
          texto:
            "O atendimento passa a ser seu. Histórico, documentos e rascunho ficam como estão, e a trilha registra a passagem.",
          acao: "Assumir",
        }))
      )
        return;
      await tentar("transferir", async () => {
        await api.editarFicha(casoAberto(), { colaborador: eu });
        await api.anotar(casoAberto(), dono ? `${eu} assumiu o atendimento (era de ${dono}).` : `${eu} assumiu o atendimento.`);
        await carregarMesa();
        await recarregarCaso();
        avisar("Atendimento assumido.");
      });
    },

    /**
     * Um campo da ficha, gravado no lugar.
     *
     * A ficha era só leitura na coluna onde ela é lida: corrigir a data de
     * admissão que a IA extraiu errado custava abrir outra tela. `editarFicha`
     * já existia (é o mesmo PATCH que a qualificação usa), e o servidor grava
     * o "de → para" de cada campo na trilha — quem mudou o salário fica
     * escrito.
     *
     * Sem `avisar`: o recibo é o `Gravou` verde na própria linha, e um aviso
     * por tecla soltaria uma torrada a cada campo corrigido.
     */
    salvarCampo: (chave, valor, idDoCaso) =>
      tentar("ficha", async () => {
        const id = idDoCaso ?? casoAberto();
        if (!id) return;
        /* Vazio apaga o campo: o servidor remove a chave quando o valor é
           nulo, e "" gravaria uma string vazia que a peça imprime como espaço. */
        await api.editarFicha(id, { [chave]: valor === "" ? null : valor });
        gravou(`campo:${chave}`, ref.current.usuario?.pessoa ?? null);
        await recarregarCaso();
        await carregarMesa();
      }),

    /** Mais alguém cuidando do caso — o titular continua sendo um só. */
    incluirResponsavel: (pessoa) =>
      tentar("transferir", async () => {
        const id = casoAberto();
        if (!id) return;
        await api.incluirResponsavel(id, pessoa);
        gravou(`acompanha:${id}`, ref.current.usuario?.pessoa ?? null);
        await recarregarCaso();
        await carregarMesa();
      }),

    removerResponsavel: async (pessoa) => {
      const id = casoAberto();
      if (!id) return;
      if (!(await confirmar({ titulo: `Tirar ${pessoa} do atendimento?`, acao: "Tirar", perigo: true }))) return;
      await tentar("transferir", async () => {
        await api.removerResponsavel(id, pessoa);
        await recarregarCaso();
        await carregarMesa();
      });
    },

    /**
     * Quem cuida do caso — uma pessoa ou várias, todas titulares.
     *
     * Substituiu o `transferirPara(nome)`, que fazia DUAS escritas na tela:
     * `editarFicha({colaborador})` e um `anotar` com a frase da trilha. Duas
     * idas para um gesto significam que um caso podia ficar transferido sem
     * linha na trilha se a segunda falhasse — e a frase da trilha morava aqui,
     * na tela, longe da regra. Agora é UMA rota, e quem escreve a trilha é o
     * domínio (`responsaveis.definir`), dentro da mesma transação da troca.
     */
    definirResponsaveis: (pessoas) =>
      tentar("transferir", async () => {
        const id = casoAberto();
        if (!id) return;
        await api.definirResponsaveis(id, pessoas);
        gravou(`acompanha:${id}`, ref.current.usuario?.pessoa ?? null);
        navRef.current.ajustar({ transferir: null });
        await carregarMesa();
        await recarregarCaso();
      }),

    /*
     * Apagar a peça aberta — o rascunho errado, não o processo.
     *
     * Peça se cria por engano (tese errada, clique a mais na esteira) e ficava
     * para sempre na esteira do MARCOS, contando como trabalho. Some com as
     * versões; o servidor recusa a protocolada, que já virou processo no
     * fórum.
     */
    apagarPeca: async (peca) => {
      if (!peca?.id) return;
      const ok = await confirmar({
        titulo: "Apagar esta peça?",
        texto: `Some a minuta${peca.versao > 1 ? ` e as ${peca.versao} versões` : ""}, junto com os apontamentos da revisão. O caso, a conversa e os fatos marcados ficam. Não dá para desfazer.`,
        acao: "Apagar peça",
        perigo: true,
      });
      if (!ok) return;
      await tentar("peca", async () => {
        await api.apagarPeca(peca.id);
        navRef.current.ajustar({ peticao: null });
        await recarregarCaso();
        await carregarMesa();
        avisar("Peça apagada.");
      });
    },

    moverEtapa: (chave) =>
      tentar("etapa", async () => {
        const id = casoAberto();
        await api.mover(id, chave, "movido pela mesa");
        await carregarMesa();
        await recarregarCaso();
        gravou(`etapa:${id}`, ref.current.usuario?.nome);
      }),

    /*
     * Tirar o atendimento da mesa — e o caminho de volta existe.
     *
     * Arquivar é o "apagar" do dia a dia: o caso some da lista e continua
     * inteiro no banco. Quem some tem de sair da tela também, senão fica uma
     * conversa aberta que a lista não mostra mais.
     */
    /* `alvo` é a linha da lista (menu do botão direito); sem ele, o caso
       aberto. O nome vem da mesa quando o alvo não é o que está aberto — a
       ficha carregada é de outro caso. */
    arquivarCaso: async (alvo) => {
      const id = alvo ?? casoAberto();
      const nome =
        (alvo ? ref.current.mesa.find((c) => c.id === alvo)?.nome : ref.current.caso?.registro?.nome) ?? "este atendimento";
      const ok = await confirmar({
        titulo: `Arquivar ${nome}?`,
        texto:
          "Sai da mesa e para de contar nas pastilhas. A conversa, os arquivos e a trilha ficam guardados, e o atendimento volta sozinho quando o cliente escrever.",
        acao: "Arquivar",
      });
      if (!ok) return;
      await tentar("arquivar", async () => {
        await api.arquivarRegistro(id);
        listaDaMesa.remover(id);
        await carregarMesa();
        /* Só volta para a lista se quem saiu da mesa era o que estava na tela:
           arquivar pelo menu de contexto de OUTRA linha não pode fechar a
           conversa que a pessoa está lendo. */
        if (id === casoAberto()) navRef.current.voltarParaLista();
        avisar(`${nome} foi arquivado.`);
      });
    },

    /*
     * A volta do arquivo — a outra metade da promessa de "dá para trazer de
     * volta". Sem pergunta: trazer para a mesa não destrói nada e se desfaz
     * arquivando de novo, na mesma linha.
     */
    desarquivarCaso: async (alvo) => {
      const id = alvo ?? casoAberto();
      const nome =
        (alvo ? ref.current.mesa.find((c) => c.id === alvo)?.nome : ref.current.caso?.registro?.nome) ?? "este atendimento";
      await tentar("desarquivar", async () => {
        await api.desarquivarRegistro(id);
        /* Quem traz de volta está quase sempre na pastilha "Arquivados": o caso
           sumia dali e não aparecia em lugar nenhum (conversa antiga fica fora
           da primeira página da mesa), e parecia que o botão não fazia nada
           (Laura, 15/10). Sai do recorte e abre o caso na mesa. */
        const arquivados = new URLSearchParams(window.location.search).get("filtro") === "Arquivados";
        if (arquivados) navRef.current.ir(`/atendimentos/${id}${navRef.current.buscaSem("filtro")}`);
        else if (id === casoAberto()) await recarregarCaso();
        await carregarMesa();
        avisar(`${nome} voltou para a mesa.`);
      });
    },

    /*
     * "Volto nisso" — a marca de não lida.
     *
     * Ela SAI da conversa depois de marcar, e isso não é enfeite: a tela marca
     * lida ao abrir o caso (ver `carregarCaso`), então ficar parado nele
     * desfaria o gesto na releitura seguinte. Quem marca de outra linha
     * (menu do botão direito) não sai de onde está.
     */
    naoLerCaso: async (alvo) => {
      const id = alvo ?? casoAberto();
      const nome =
        (alvo ? ref.current.mesa.find((c) => c.id === alvo)?.nome : ref.current.caso?.registro?.nome) ?? "este atendimento";
      await tentar("nao-lida", async () => {
        /* Sai ANTES de marcar: com a conversa aberta, a releitura que o evento
           de não lida provoca marcaria lida de novo. */
        if (id === casoAberto()) navRef.current.voltarParaLista();
        const r = await api.marcarNaoLida(id);
        await carregarMesa();
        avisar(r.marcadas ? `${nome} voltou para não lida.` : `${nome} não tem mensagem do cliente para marcar.`);
      });
    },

    /*
     * Apagar de vez — a saída para o lixo que o WhatsApp cria.
     *
     * Canal de anúncio, número errado e teste viram ficha no funil, e arquivar
     * deixa esse lixo lá para sempre. O servidor é quem recusa apagar caso com
     * peça, contrato assinado ou processo; aqui a confirmação diz o tamanho do
     * que se perde ANTES, com a contagem de mensagens na mão.
     */
    apagarCaso: async (alvo) => {
      const id = alvo ?? casoAberto();
      const daMesa = alvo ? ref.current.mesa.find((c) => c.id === alvo) : null;
      const dados = alvo ? null : ref.current.caso;
      const nome = daMesa?.nome ?? dados?.registro?.nome ?? "este atendimento";
      /* A contagem só existe para o caso ABERTO (a mesa não traz o total de
         mensagens por linha): apagando pelo menu de contexto, a frase diz o
         que se perde sem inventar número. */
      const quantas = dados?.mensagens?.length ?? null;
      const ok = await confirmar({
        titulo: `Apagar ${nome} de vez?`,
        /* O servidor não recusa mais caso com peça, contrato ou processo — então
           a frase tem de dizer o tamanho do estrago ANTES, que é onde a decisão
           acontece agora. Prometer menos do que se apaga é o pior jeito de falhar. */
        texto: `Some TUDO: a ficha, ${quantas == null ? "a conversa" : `${quantas} mensagem(ns)`}, os prazos, a trilha, as peças da esteira, os contratos assinados, as gravações e a pasta de documentos no disco. Não dá para desfazer — para tirar da mesa sem perder nada, use Arquivar. Fica registrado quem apagou e o que a linha levou junto.`,
        acao: "Apagar de vez",
        perigo: true,
      });
      if (!ok) return;
      await tentar("apagar", async () => {
        /* "Não encontrado" aqui é o SEGUNDO clique, não uma falha: o registro
           já saiu. Tratar como erro deixava o toast vermelho na tela e, pior,
           pulava o `voltarParaLista()` — a conversa do apagado ficava aberta. */
        await api.apagarRegistro(id, "apagado na mesa").catch((e) => {
          if (!/não encontrado/i.test(e.message)) throw e;
        });
        listaDaMesa.remover(id);
        await carregarMesa();
        if (id === casoAberto()) navRef.current.voltarParaLista();
        avisar(`${nome} foi apagado.`);
      });
    },

    /**
     * O gesto em MASSA: a mesma ação nos atendimentos marcados.
     *
     * Uma função só, e genérica, porque o que muda entre arquivar trinta e
     * etiquetar trinta é só a chamada do servidor — o resto (marcar ocupado,
     * traduzir a falha, reler a mesa, fechar a conversa que saiu de cena) é
     * igual nas cinco ações, e escrever isso cinco vezes é como quatro delas
     * esquecem de fechar a conversa.
     *
     * O RECADO fica com quem chamou: em lote a frase depende do que voltou
     * (`{ feitos, falhas }`), e "deu erro" sobre trinta casos não diz quais.
     */
    emLote: async (nome, ids, fazer, { saem = false } = {}) => {
      const r = await tentar(nome, () => fazer(ids));
      if (!r) return null;
      /* Quem saiu da mesa sai da lista ANTES da releitura: senão a linha
         arquivada fica em cena até `/api/mesa` responder, e quem apertou acha
         que o gesto não pegou. */
      if (saem) for (const id of ids) listaDaMesa.remover(id);
      await carregarMesa();
      /* A conversa aberta não pode continuar de pé sobre um caso que saiu. */
      if (saem && ids.includes(navRef.current.casoId)) navRef.current.voltarParaLista();
      return r;
    },

    novoCaso: async () => {
      const nome = await pedirTexto({ titulo: "Atendimento novo", convite: "Nome do cliente", acao: "Criar" });
      if (!nome) return;
      /* Telefone é opcional: o caso nasce da ligação e o número chega depois. */
      const telefone =
        (await pedirTexto({
          titulo: `Telefone de ${nome}`,
          texto: "Com DDD. Pode ficar em branco e entrar depois.",
          convite: "(11) 90000-0000",
          acao: "Criar",
          opcional: true,
        })) ?? "";
      await tentar("novo", async () => {
        const r = await api.mandar("/api/records", { nome, telefone });
        await carregarMesa();
        navRef.current.irParaCaso(r.id);
      });
    },

    /* O cartão de contato da conversa vira atendimento: o servidor abre o caso
       do número ou cria (`leads.casoDoContato`), e a tela vai até ele. */
    abrirDoContato: (contato) =>
      tentar("novo", async () => {
        const r = await api.mandar("/api/leads/do-contato", contato);
        if (r.novo) await carregarMesa();
        navRef.current.irParaCaso(r.id);
      }),

    /* Pergunta ao SERVIDOR quem é o próximo sem responsável: procurar na página
       carregada dizia "nada na fila" com quarenta na fila, porque o recorte de
       quem está olhando não é o recorte da fila. */
    puxarFila: () =>
      tentar("fila", async () => {
        const { corpo } = await api.mesaPagina({ offset: 0, limite: 1, filtro: "Fila" });
        const proximo = (corpo.atendimentos ?? [])[0];
        if (!proximo) return avisar("Nada na fila.");
        return navRef.current.irParaCaso(proximo.id);
      }),
  };
}
